import type { ExecutionHistoryEntry } from "../components";

/**
 * Compare parameter objects for execution state invalidation
 */
export function parametersMatch(
  params1: Record<string, unknown>,
  params2: Record<string, unknown>,
): boolean {
  return JSON.stringify(params1) === JSON.stringify(params2);
}

/**
 * Generate execution ID for MCP requests
 */
export function generateExecutionId(method: string): string {
  const timestamp = Date.now();
  return `${method}:${timestamp}`;
}

/**
 * Create MCP request body for tool execution
 */
export function createToolExecutionRequest(
  toolName: string,
  args: Record<string, unknown>,
  executionId?: string,
) {
  return {
    jsonrpc: "2.0" as const,
    id: executionId || generateExecutionId("tools/call"),
    method: "tools/call",
    params: {
      name: toolName,
      arguments: args,
    },
  };
}

/**
 * Create MCP request body for prompt execution
 */
export function createPromptExecutionRequest(
  promptName: string,
  args: Record<string, unknown>,
  executionId?: string,
) {
  return {
    jsonrpc: "2.0" as const,
    id: executionId || generateExecutionId("prompts/get"),
    method: "prompts/get",
    params: {
      name: promptName,
      arguments: args,
    },
  };
}

/**
 * Extract result from MCP response
 */
export function extractMcpResult(payload: unknown) {
  const data = payload as {
    result?: unknown;
    error?: unknown;
  };
  return data?.result ?? data;
}

/**
 * Create execution history entry
 */
export function createExecutionHistoryEntry(options: {
  id: string;
  startTime: number;
  parameters?: Record<string, unknown>;
  arguments?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  status: "success" | "error";
}) {
  const {
    id,
    startTime,
    parameters,
    arguments: args,
    result,
    error,
    status,
  } = options;
  const duration = Date.now() - startTime;

  const entry: ExecutionHistoryEntry = {
    id,
    timestamp: new Date(startTime).toISOString(),
    status,
    duration,
  };

  if (parameters) {
    entry.parameters = parameters;
  }
  if (args) {
    entry.arguments = args;
  }
  if (result !== undefined) {
    entry.result = result;
  }
  if (error) {
    entry.error = error;
  }

  return entry;
}
